// Libraries
import {
    memo, useContext, useEffect, useMemo, 
    useState
} from 'react';
import {
    useTheme 
} from '@mui/material';

// Utils
import {
    createEmailSkip, createNameSkip, createPhoneSkip 
} from '../../../config/utils';

// Config
import {
    SOBERISH_MOM_DRYJAN_CHALLENGES
} from '../../../config/types';
import {
    defaultSignupSections,
    DiagnosticFormContext 
} from '../../../../../contexts/form';
import {
    SoberishMomChallengeTitle,
    SoberishMomDryishJanuary2025ChallengeIcon
} from './graphics';
import {
    useCookies 
} from 'react-cookie';

// Components
import SCREENS from '../sections';

// opt in flow for soberish mom dryish january challenge
// v2 of new diagnostic
// moved up email and name submission
// removed intro animation
// updated drink baselines screen
// shifted phone entry to start of plan section
// removed phone permission screen
// shortened the plan section
// joint premium checkout

type Props = {
    index: number;
};

export default memo(function DiagnosticFormABCFitnessDryishJanuary2025({
    index = 0 
}: Props) {
    const diagnosticFormContext = useContext(DiagnosticFormContext);
    const theme = useTheme();
    const [cookies] = useCookies();
    const [isLoading, setIsLoading] = useState(true);
    const skippySkipsName = useMemo(() => createNameSkip(diagnosticFormContext, 12), [diagnosticFormContext]);
    const skippySkipsEmail = useMemo(() => createEmailSkip(diagnosticFormContext, 23), [diagnosticFormContext]);
    const skippySkipsPhone = useMemo(() => createPhoneSkip(diagnosticFormContext, 11), [diagnosticFormContext]);

    // Set initial values and functions in context
    useEffect(() => {
        diagnosticFormContext.skippySkipsName = skippySkipsName;
        diagnosticFormContext.skippySkipsEmail = skippySkipsEmail;
        diagnosticFormContext.skippySkipsPhone = skippySkipsPhone;
        diagnosticFormContext.sections = [
            {
                key: 'challenge',
                headerText: 'Set Up Your Challenge',
                length: 1,
                colors: {
                    background: theme.palette.soberishMom2025['100'],
                    main: theme.palette.soberishMom2025.main,
                    headerText: theme.palette.soberishMom2025.dark,
                    choiceText: theme.palette.soberishMom2025.dark,
                    choice: theme.palette.soberishMom2025.light,
                    ctaBackground: theme.palette.transparent,
                    ctaButton: theme.palette.soberishMom2025.main,
                    ctaButtonText: theme.palette.white,
                    ctaText: theme.palette.neutral.main,
                    buttonArrow: theme.palette.white,
                    circleTakeover: theme.palette.soberishMom2025['100'],
                    trophy: theme.palette.soberishMom2025.main
                },
                icon: <SoberishMomChallengeTitle />,
                inProgressBar: false,
                challengeName: 'Soberish Mom Dry(ish) January',
                challengeGraphic: <SoberishMomDryishJanuary2025ChallengeIcon />,
                challengeDescription: 'Ready to kick the new year off on the right foot? Join the most popular way to reset your drinking in the new year, whether you want to go fully dry or just cut back a bit, it\'s up to you!',
                challengeOptions: SOBERISH_MOM_DRYJAN_CHALLENGES,
                challengeIdentifier: 'soberishmom-dryjan-2025',
                hasTrophy: false
            },
            ...defaultSignupSections
        ];
        const checkoutSection = diagnosticFormContext.sections.find(s => s.key === 'checkout');
        checkoutSection.colors = {
            ...checkoutSection.colors,
            ctaBackground: theme.palette.success.light,
            ctaButton: theme.palette.success.main,
        };
        diagnosticFormContext.sections[1].length = 8;
        diagnosticFormContext.sections[2].length = 19;
        diagnosticFormContext.sections[3].length = 5;
        diagnosticFormContext.sections[4].length = 20;
        setIsLoading(false);
    }, []);

    useEffect(() => {
        const trackingName = screens[index].trackingName;
        const screenName = screens[index].screenName;

        if (trackingName) diagnosticFormContext.fireTrackingEvents(diagnosticFormContext, trackingName, cookies);
        if (screenName) diagnosticFormContext.currentScreenName = screenName;
    }, [index]);

    const screens = useMemo(() => ([
        ...SCREENS.CHALLENGE_SCREENS,
        ...SCREENS.GOALS_SCREENS_V2,
        ...SCREENS.HABITS_SCREENS_V3,
        ...SCREENS.ABOUT_YOU_SCREENS_V2,
        ...SCREENS.PLAN_SCREENS_V2,
        ...SCREENS.CHECKOUT_SCREENS_V3
    ]), []);
    
    if (isLoading) return <></>;

    return (
        <>
            {screens[index].component}
        </>
    );
});
